import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getResource } from "../../data/request";
import { OfficerDetails } from "../../types/OfficerDetails";
import * as Utilities from "../../data/utilities";
import { AppointmentList } from "./AppointmentList";
import SearchBox from "../Components/SearchBox";
import { ItemCard } from "../Components/ItemCard";
import { FaMapMarkerAlt } from "react-icons/fa";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY } from "../colors";
import { Helmet } from "react-helmet";

export const OfficerDetailsPage = () => {
  const params = useParams();

  const [officersDetails, setOfficerDetails] = useState<OfficerDetails>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (params.resourceId == undefined) {
        return;
      }
      setLoading(true);
      try {
        const doc = await getResource(
          "/officers/" + params.resourceId + "/appointments"
        );
        setOfficerDetails(doc ? translate(doc) : undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [params.resourceId]);

  const translate = (json: any) => {
    let nationality = "Unknown";
    let country_of_residence = "Unknown";
    let addresses: any = [];

    (json["items"] ?? []).forEach(function (item: any) {
      if (item["nationality"] != undefined) {
        nationality = item["nationality"];
      }

      if (item["country_of_residence"] != undefined) {
        country_of_residence = item["country_of_residence"];
      }

      addresses.push(Utilities.getAddressDispay(item["address"]));
    });

    const addresses_unique: string[] = Array.from(new Set(addresses));

    let dob = "Unknown";
    if (json["date_of_birth"] != undefined) {
      dob =
        Utilities.MONTHS[json["date_of_birth"]["month"] - 1] +
        " " +
        json["date_of_birth"]["year"];
    }

    json["my_nationality"] = nationality;
    json["my_country_of_residence"] = country_of_residence;
    json["my_addresses"] = addresses_unique;
    json["my_dob"] = dob;
    return json;
  };

  return (
    <Box>
      <Helmet>
        <title>{"Directory - " + officersDetails?.name}</title>
        {officersDetails && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Person",
              name: officersDetails.name,
            })}
          </script>
        )}
      </Helmet>

      <Box style={{ marginBottom: 24 }}>
        <SearchBox />
      </Box>

      {loading ? (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : officersDetails !== undefined ? (
        <Box>
          <Paper style={{ padding: 24, marginBottom: 16 }} elevation={0}>
            <Typography variant="h5" style={{ marginBottom: 8 }}>
              {officersDetails.name}
            </Typography>

            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 20,
                padding: 10,
              }}
            >
              <ItemCard item={{ title: "Date of Birth", subtitle: officersDetails.my_dob, icon: "date" }} />
              <ItemCard item={{ title: "Nationality", subtitle: officersDetails.my_nationality, icon: "nationality" }} />
              <ItemCard
                item={{ title: "Country of Residence", subtitle: officersDetails.my_country_of_residence, icon: "jurisdiction" }}
              />
            </Box>

            {officersDetails.my_addresses.length > 0 && (
              <Box style={{ padding: 10, marginTop: 10 }}>
                <Typography variant="subtitle2" style={{ color: TEXT_SECONDARY, marginBottom: 8 }}>
                  {officersDetails.my_addresses.length > 1 ? "Addresses" : "Address"}
                </Typography>
                <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {officersDetails.my_addresses.map((address: string) => (
                    <Box key={address} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <FaMapMarkerAlt size={16} color={DELTA_ACCENT} style={{ marginTop: 3, flexShrink: 0 }} />
                      <Typography variant="body2" style={{ color: DELTA_PRIMARY }}>
                        {address}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          <Paper style={{ padding: 24 }} elevation={0}>
            <Typography variant="h6" style={{ marginBottom: 12 }}>
              Appointments
            </Typography>
            <AppointmentList data={officersDetails.items}></AppointmentList>
          </Paper>
        </Box>
      ) : (
        <Paper style={{ padding: 24 }} elevation={0}>
          <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
            No officer details found.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
